import { useState, type FormEvent } from "react";

import {
  ApiError,
  type ApiDeps,
  type InvoiceDrilldown,
  fetchInvoiceDetail,
} from "../lib/api";
import { t, type Language } from "../lib/i18n";
import { formatMoney } from "../lib/money";
import { ReasoningTrace } from "./ReasoningTrace";

interface InvoiceSearchProps {
  deps: ApiDeps;
  language: Language;
}

type SearchState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "found"; result: InvoiceDrilldown }
  | { kind: "not_found" }
  | { kind: "error" };

/** Paste-a-correlation-id drill-down (per F37 spec §1: "what did the
 * agent do with this invoice?"). Submitting calls the backend's
 * indexed `/invoice/{id}` route and renders the action chain plus the
 * F17a reasoning trace. A 404 (`ApiError`) maps to a friendly
 * not-found note; other failures show a generic retry message. */
export function InvoiceSearch({
  deps,
  language,
}: InvoiceSearchProps): JSX.Element {
  const [query, setQuery] = useState("");
  const [state, setState] = useState<SearchState>({ kind: "idle" });

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const id = query.trim();
    if (!id) return;
    setState({ kind: "loading" });
    fetchInvoiceDetail(deps, id)
      .then((result) => setState({ kind: "found", result }))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: "not_found" });
          return;
        }
        setState({ kind: "error" });
      });
  };

  return (
    <section
      aria-label={t("search.heading", language)}
      className="rounded-[2px] border border-kwanza-divider bg-kwanza-card p-4 shadow-sm"
    >
      <h2 className="mb-1 text-base font-semibold text-kwanza-ink">
        {t("search.heading", language)}
      </h2>
      <p className="mb-3 text-sm text-kwanza-inkSoft">
        {t("search.hint", language)}
      </p>
      <form onSubmit={onSubmit} className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("search.placeholder", language)}
          aria-label={t("search.placeholder", language)}
          className="flex-1 rounded border border-kwanza-divider px-3 py-2 font-mono text-sm text-kwanza-ink"
          data-testid="search-input"
        />
        <button
          type="submit"
          className="rounded-md bg-kwanza-accent px-4 py-2 text-sm font-bold text-kwanza-onAccent"
          data-testid="search-submit"
        >
          {t("search.submit", language)}
        </button>
      </form>

      {/* A9 (WCAG 4.1.3) — the search status (loading / not-found / error)
       * is announced via an aria-live region so a screen-reader user knows
       * the outcome without re-reading the form. The found result renders
       * outside the live region (it's a navigable block the user explicitly
       * requested, not a transient status). */}
      <div aria-live="polite" role="status">
        {state.kind === "loading" ? (
          <p className="mt-3 text-sm text-kwanza-inkSoft" aria-busy="true">
            {t("search.loading", language)}
          </p>
        ) : state.kind === "not_found" ? (
          <div className="mt-3" data-testid="search-not-found">
            <p className="text-sm font-medium text-kwanza-ink">
              {t("search.not_found.title", language)}
            </p>
            <p className="text-sm text-kwanza-inkSoft">
              {t("search.not_found.body", language)}
            </p>
          </div>
        ) : state.kind === "error" ? (
          <div className="mt-3" data-testid="search-error">
            <p className="text-sm font-medium text-kwanza-alert">
              {t("search.error.title", language)}
            </p>
            <p className="text-sm text-kwanza-inkSoft">
              {t("search.error.body", language)}
            </p>
          </div>
        ) : null}
      </div>
      {state.kind === "found" ? (
        <InvoiceResult result={state.result} language={language} />
      ) : null}
    </section>
  );
}

function _row(label: string, value: string | null): { label: string; value: string } {
  return { label, value: value && value !== "" ? value : "—" };
}

/** The drill-into-invoice detail render — the joined action chain as a
 * definition list + the F17a reasoning trace. Exported so the new
 * InvoiceList drill-in (Task 5) reuses the exact same detail body the
 * reference-search path shows (no drift between the two entry points). */
export function InvoiceResult({
  result,
  language,
}: {
  result: InvoiceDrilldown;
  language: Language;
}): JSX.Element {
  const d = result.detail;
  const amount = d.amount ? formatMoney(d.amount, language) : null;
  const confidence =
    d.classification_confidence === null
      ? null
      : `${Math.round(d.classification_confidence * 100)}%`;
  const filedAt = d.filed_at ? new Date(d.filed_at).toLocaleString(language) : null;

  const rows = [
    _row(t("invoice.vendor", language), d.vendor_display_name ?? d.vendor_slug),
    _row(t("invoice.invoice_number", language), d.invoice_number),
    _row(t("invoice.invoice_date", language), d.invoice_date),
    _row(t("invoice.amount", language), amount),
    _row(t("invoice.filed_at", language), filedAt),
    _row(t("invoice.sharepoint", language), d.sharepoint_path),
    _row(t("invoice.planner_bucket", language), d.planner_bucket),
    _row(t("invoice.method", language), d.classification_method),
    _row(t("invoice.confidence", language), confidence),
  ];

  return (
    <div className="mt-4" data-testid="search-result">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-col">
            <dt className="text-xs uppercase tracking-wide text-kwanza-inkSoft">
              {r.label}
            </dt>
            <dd className="break-words text-sm text-kwanza-ink">{r.value}</dd>
          </div>
        ))}
      </dl>
      <ReasoningTrace
        trace={result.reasoning_trace}
        visible={result.reasoning_trace_visible}
        language={language}
      />
    </div>
  );
}
